import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsBox } from '../common/basic/QcsBox';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { HtmlViewer } from '../common/html/HtmlViewer';
import { SignUser } from '../common/SignUser';
import { TourniquetButtonContent } from '../tourniquet/TourniquetButtonContent';
import { TourniquetSlideCard } from '../tourniquet/TourniquetStyles';
import { trainingVersionService } from '@api/services/services';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { QcsTypography } from '../common/basic/QcsTypography';
import QcsAlert from '../common/basic/QcsAlert';
import { resources } from '@api/resources';

interface Props {
  versionId: string;
  lang: string;
  persons: ReferenceDto[];
  hasQuestionnaire: boolean;
  afterTrainingContent?: string;
  isPlayground?: boolean;
  questionnaireResult?: { score: number; requiredScore: number };
  questionnaireAnswers?: Array<{ questionId: string; answerId: string }>;
  onPrev: () => void;
  onNext: () => void;
  onAbort: () => void;
}

export const TrainingRunSign: FC<Props> = ({
  versionId,
  lang,
  persons,
  hasQuestionnaire,
  afterTrainingContent,
  isPlayground,
  questionnaireResult,
  questionnaireAnswers,
  onPrev,
  onNext,
  onAbort,
}) => {
  const { t } = useTranslation();
  const [remainingPersons, setRemainingPersons] = useState<ReferenceDto[]>(persons);

  const allSigned = remainingPersons.length === 0;
  const completedRef = useRef(false);

  useEffect(() => {
    // with after training content, wait for user confirmation instead of completing immediately
    if (allSigned && !hasQuestionnaire && !afterTrainingContent && !completedRef.current) {
      completedRef.current = true;
      onNext();
    }
  }, [allSigned, hasQuestionnaire, afterTrainingContent, onNext]);

  const handleSignSave = async (userId: string, documentId: string) => {
    if (!isPlayground) {
      await trainingVersionService.signTraining(versionId, {
        person: userId,
        signatureDocumentId: documentId,
        trainingLanguageVariant: lang,
        ...(questionnaireAnswers ? { questionnaireLanguageVariant: lang, answers: questionnaireAnswers } : { answers: [] }),
      });
    }
    setRemainingPersons((prev) => prev.filter((x) => x.id !== userId));
  };

  return (
    <TourniquetButtonContent backOnClick={onPrev} onAbort={hasQuestionnaire || !allSigned ? onAbort : undefined} mobileGap={true}>
      <TourniquetSlideCard sx={{ display: 'flex', flexDirection: 'column' }}>
        <QcsBox sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          {questionnaireResult && (
            <>
              <QcsTypography sx={{ mb: 1 }} align="center">
                {t('tourniquet.yourScore')}
                {': '}
                {questionnaireResult.score}%
              </QcsTypography>
              {!!questionnaireResult.requiredScore && (
                <QcsTypography variant="caption" sx={{ mb: 1, display: 'block' }} align="center">
                  {t('tourniquet.minimalRequiredScore')}
                  {': '}
                  {questionnaireResult.requiredScore}%
                </QcsTypography>
              )}
              <QcsAlert severity="success" sx={{ margin: '0 auto', marginBottom: '1rem' }}>
                {t('tourniquet.successQuestionnaire')}
              </QcsAlert>
            </>
          )}
          {!!afterTrainingContent && !hasQuestionnaire && (
            <QcsBox sx={{ marginBottom: '1rem' }}>
              <HtmlViewer html={afterTrainingContent} />
            </QcsBox>
          )}
          {!allSigned && (
            <SignUser playground={isPlayground} userList={remainingPersons} save={handleSignSave} errorResource={[resources.signTraining]} />
          )}
          {allSigned && !hasQuestionnaire && !!afterTrainingContent && (
            <QcsBox sx={{ display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
              <QcsLoadingButton variant="contained" onClick={onNext} resourceLoading={resources.signTraining}>
                {t('common.close')}
              </QcsLoadingButton>
            </QcsBox>
          )}
        </QcsBox>
      </TourniquetSlideCard>
    </TourniquetButtonContent>
  );
};
